import { lazy } from 'react';
import { Navigate, useParams } from 'react-router-dom';

// project imports
import AuthLayout from 'layout/Auth';
import Loadable from 'components/Loadable';
import { APP_AUTH, APP_DEFAULT_PATH, AuthProvider } from 'config';
import useAuth from 'hooks/useAuth';

// Redirect /:uniqueCode/ → dashboard (if logged in) or /:uniqueCode/login (if not)
function UniqueCodeIndex() {
  const { uniqueCode } = useParams<{ uniqueCode: string }>();
  const { isLoggedIn, user } = useAuth();

  if (isLoggedIn) {
    if (user?.userType === 2) return <Navigate to="/tenant-dashboard" replace />;
    if (user?.userType === 4) return <Navigate to="/contractor-dashboard" replace />;
    return <Navigate to={APP_DEFAULT_PATH} replace />;
  }

  return <Navigate to={`/${uniqueCode}/login`} replace />;
}

// jwt auth pages
const JwtAuthLogin = Loadable(lazy(() => import('pages/auth/jwt/login')));
const JwtAuthRegister = Loadable(lazy(() => import('pages/auth/jwt/register')));
const JwtAuthForgotPassword = Loadable(lazy(() => import('pages/auth/jwt/forgot-password')));
const JwtAuthTenantForgotPassword = Loadable(lazy(() => import('pages/auth/jwt/tenant-forgot-password')));
const JwtAuthTenantResetPassword = Loadable(lazy(() => import('pages/auth/jwt/tenant-reset-password')));
const JwtAuthTenantCheckMail = Loadable(lazy(() => import('pages/auth/jwt/tenant-check-mail')));
const JwtAuthResetPassword = Loadable(lazy(() => import('pages/auth/jwt/reset-password')));
const JwtAuthCheckMail = Loadable(lazy(() => import('pages/auth/jwt/check-mail')));
const JwtAuthInvalidCode = Loadable(lazy(() => import('pages/auth/jwt/invalid-code')));
const JwtAuthTwoFA = Loadable(lazy(() => import('pages/auth/jwt/twoFA')));
const JWTAuthTenantLogin = Loadable(lazy(() => import('pages/auth/jwt/tenant-login')));
const ESignCompleted = Loadable(lazy(() => import('pages/auth/jwt/esign-completed')));
const ESignSessionExpired = Loadable(lazy(() => import('pages/auth/jwt/esign-expired')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const RouteErrorBoundary = Loadable(lazy(() => import('pages/maintenance/RouteErrorBoundary')));
const GenerateInvoice = Loadable(lazy(() => import('pages/auth/jwt/generate-invoice')));

// ==============================|| AUTH ROUTING ||============================== //

const LoginRoutes = {
  path: '/',
  errorElement: <RouteErrorBoundary />,
  children: [
    {
      element: <AuthLayout />,
      children: [
        {
          path: APP_AUTH === AuthProvider.JWT ? '/' : 'jwt',
          children: [
            // -------- Normal Auth Routes --------
            { path: '/', element: <JwtAuthLogin /> },
            { path: 'login', element: <JwtAuthLogin /> },
            { path: 'register', element: <JwtAuthRegister /> },
            { path: 'forgot-password', element: <JwtAuthForgotPassword /> },
            { path: 'check-mail', element: <JwtAuthCheckMail /> },
            { path: 'invalid-code', element: <JwtAuthInvalidCode /> },
            { path: 'reset-password', element: <JwtAuthResetPassword /> },
            { path: 'verify-otp', element: <JwtAuthTwoFA /> },
            { path: 'error', element: <MaintenanceError500 /> },

            // -------- Tenant Auth Routes --------
            {
              path: ':uniqueCode',
              children: [
                { index: true, element: <UniqueCodeIndex /> },
                { path: 'login', element: <JWTAuthTenantLogin /> },
                { path: 'register', element: <JwtAuthRegister /> },
                { path: 'forgot-password', element: <JwtAuthTenantForgotPassword /> },
                { path: 'check-mail', element: <JwtAuthTenantCheckMail /> },
                { path: 'invalid-code', element: <JwtAuthInvalidCode /> },
                { path: 'reset-password', element: <JwtAuthTenantResetPassword /> },
                { path: 'verify-otp', element: <JwtAuthTwoFA /> },
                { path: 'error', element: <MaintenanceError500 /> }
              ]
            }
          ]
        }
      ]
    },
    // -------- ESign Unauthenticated Routes --------
    // URL: /ESignCompleted?bn_session_id={SessionID}
    { path: 'ESignCompleted', element: <ESignCompleted /> },
    // URL: /ESignSessionExpired?bn_session_id={SessionID}
    { path: 'ESignSessionExpired', element: <ESignSessionExpired /> },
    { path: 'GenerateInvoice', element: <GenerateInvoice /> },
  ]
};

export default LoginRoutes;
